import React, { useEffect, useState } from 'react';
import {
  Card, CardHeader, CardContent, Grid, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, IconButton, Snackbar, Dialog,
  DialogTitle, DialogContent, DialogActions, MenuItem, FormControl, Select, Box,
  Divider, Typography, Avatar, InputAdornment, CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import Swal from 'sweetalert2';

const apiUrl = 'https://apidashboard.eurekagroup.id/bu';
const addBuUrl = `${apiUrl}/add`;
const editBuUrl = `${apiUrl}/edit`;
const deleteBuUrl = (id) => `${apiUrl}/delete/${id}`;

const BisnisUnitDashPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successPopup, setSuccessPopup] = useState(false);
  const [message, setMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedBu, setSelectedBu] = useState(null);
  const [buCode, setBuCode] = useState('');
  const [buName, setBuName] = useState('');
  const [isActive, setIsActive] = useState(1);

  useEffect(() => {
    const fetchBisnisUnits = async () => {
      try {
        setLoading(true);
        const response = await axios.get(apiUrl);
        setData(response.data);
      } catch (error) {
        console.error('Error fetching Bisnis Units data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBisnisUnits();
  }, []);

  const handleSearchChange = (event) => setSearchText(event.target.value);
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (bu) => {
    if (bu) {
      setSelectedBu(bu);
      setBuCode(bu.bu_code);
      setBuName(bu.bu_name);
      setIsActive(bu.is_active);
    } else {
      setSelectedBu(null);
      setBuCode('');
      setBuName('');
      setIsActive(1);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleSaveBu = async () => {
    // Validation check
    if (!buCode || !buName || isActive === '') {
      setMessage('Please fill in all required fields');
      setSuccessPopup(true); // Show Snackbar on validation error
      return; // Prevent saving if validation fails
    }
  
    const buData = { bu_code: buCode, bu_name: buName, is_active: isActive };
    const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
    try {
      // Assume selectedBu is set if editing
      if (selectedBu) {
        await axios.put(editBuUrl, 
          { ...buData, id: selectedBu._id },
          {
            headers: {
              Authorization: `Bearer ${token}` // Tambahkan header Authorization
            }
          }
        );
        setData(data.map((bu) => (bu._id === selectedBu._id ? { ...bu, ...buData } : bu)));
        setMessage('Bisnis Unit updated successfully');
      } else {
        const response = await axios.post(addBuUrl, 
          buData,
          {
            headers: {
              Authorization: `Bearer ${token}` // Tambahkan header Authorization
            }
          }
        );
        setData([...data, response.data]);
        setMessage('Bisnis Unit added successfully');
      }
      setSuccessPopup(true);
    } catch (error) {
      console.error('Error saving Bisnis Unit:', error);
      setMessage('Error saving Bisnis Unit');
      setSuccessPopup(true); // Show Snackbar for error
    }
  
    handleCloseDialog();
  };  

  const handleCloseSnackbar = () => {
    setSuccessPopup(false);
  };

  const handleDeleteClick = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
    });
  
    if (result.isConfirmed) {
      const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
      try {
        await axios.delete(deleteBuUrl(id), {
          headers: {
            Authorization: `Bearer ${token}` // Tambahkan header Authorization
          }
        });
        setData(data.filter((bu) => bu._id !== id));
        setMessage('Bisnis Unit deleted successfully');
        setSuccessPopup(true);
      } catch (error) {
        console.error('Error deleting Bisnis Unit:', error);
        setMessage('Error deleting Bisnis Unit');
        setSuccessPopup(true); // Tampilkan pesan error
      }
    }
  };  


  const filteredData = data.filter((bu) => bu.bu_name.toLowerCase().includes(searchText.toLowerCase()));

  return (
    <>
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <BusinessIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Manajemen Unit Bisnis
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola unit bisnis dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
        action={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<RefreshIcon />} 
              onClick={() => window.location.reload()}
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />} 
              onClick={() => handleOpenDialog(null)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              ✨ Tambah Unit Bisnis
            </Button>
          </Box>
        }
        sx={{ pb: 2 }}
      />
      <Divider />
      <CardContent>
        {/* Filter Section */}
        <Box sx={{ 
          mb: 3, 
          p: 3, 
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200'
        }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cari Unit Bisnis"
                variant="outlined"
                value={searchText}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {filteredData.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Unit Bisnis
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Table Section */}
        <Box sx={{ 
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 400,
              flexDirection: 'column',
              gap: 2
            }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Memuat data unit bisnis...
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        No
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Kode Unit Bisnis
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Nama Unit Bisnis
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textAlign: 'center'
                      }}>
                        Aksi
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((bu, index) => (
                      <TableRow 
                        key={bu._id}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                            transform: 'scale(1.001)',
                            transition: 'all 0.2s ease'
                          },
                          '&:nth-of-type(even)': {
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {index + 1 + page * rowsPerPage}
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {bu.bu_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {bu.bu_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ 
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: bu.is_active === 1 ? 'success.light' : 'error.light',
                            color: bu.is_active === 1 ? 'success.dark' : 'error.dark',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {bu.is_active === 1 ? 'Aktif' : 'Tidak Aktif'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <IconButton 
                              onClick={() => handleOpenDialog(bu)} 
                              sx={{ 
                                color: 'info.main',
                                '&:hover': { 
                                  bgcolor: 'info.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              onClick={() => handleDeleteClick(bu._id)} 
                              sx={{ 
                                color: 'error.main',
                                '&:hover': { 
                                  bgcolor: 'error.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'grey.200',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                }}
              />
            </>
          )}
        </Box>
      </CardContent>
    </Card>
    </div>

      <Snackbar open={successPopup} autoHideDuration={6000} onClose={() => setSuccessPopup(false)} message={message} />

      <Dialog open={openDialog} onClose={() => {} }>
        <DialogTitle>{selectedBu ? 'Edit Bisnis Unit' : 'Add Bisnis Unit'}</DialogTitle>
        <DialogContent>
  <TextField
    autoFocus
    margin="dense"
    label="BU Code"
    type="text"
    fullWidth
    variant="outlined"
    value={buCode}
    onChange={(e) => setBuCode(e.target.value)}
    required
    error={!buCode}  // Error if the value is empty
    helperText={!buCode ? "This field is required" : ""}
  />

  <TextField
    margin="dense"
    label="BU Name"
    type="text"
    fullWidth
    variant="outlined"
    value={buName}
    onChange={(e) => setBuName(e.target.value)}
    required
    error={!buName}  // Error if the value is empty
    helperText={!buName ? "This field is required" : ""}
  />

  <FormControl fullWidth margin="dense" required error={isActive === ''}>
    <Select value={isActive} onChange={(e) => setIsActive(e.target.value)}>
      <MenuItem value={1}>Active</MenuItem>
      <MenuItem value={0}>Inactive</MenuItem>
    </Select>
    {isActive === '' && <FormHelperText>This field is required</FormHelperText>}
  </FormControl>
</DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">Cancel</Button>
          <Button onClick={handleSaveBu} color="primary">Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BisnisUnitDashPage;
